import { useEffect } from 'react';
import { FormDataType } from 'contexts/FormProvider';
import { useAddressChange, createAddressChangeHandler } from './useAddressChange';
import { ADDRESS_FIELD_MAPS } from 'components/AddressSearch/addressHelpers';
import { AUSTRALIA } from 'utils/constants';
import { UseFinancialContactAddressHandlerParams, AddressValues } from 'types/financialContactAddressHandler';

// TODO: QAS_CLEANUP - Change return type to void after DcomCustQasMigrationToggle decommission (currently returns AddressValues)
export const useFinancialContactAddressHandler = ({
  register,
  triggerValidation,
  watch,
  getValues,
  setValue,
  clearErrors,
  errors,
  DcomCustQasMigrationToggle,
  nominatedRepType,
}: UseFinancialContactAddressHandlerParams): AddressValues => {
  // TODO: QAS_CLEANUP - Remove all code below until "Address management integration" comment after toggle decommission - START
  const useQasLegacyLogic = !DcomCustQasMigrationToggle;

  /**
   * Registers physical and postal address form fields when using legacy QAS logic.
   * This ensures all address-related fields are available in the form state for validation and submission.
   */
  useEffect(() => {
    if (useQasLegacyLogic) {
      register('physicalAddress');
      register('physicalAddressObject');
      register('phyStreetAddress');
      register('phySuburb');
      register('phyCountry');
      register('phyPostcode');
      register('isPhysAddressValid');
      register('isManualPhysAddress');
      register('postalAddress');
      register('postalAddressObject');
      register('posStreetAddress');
      register('posSuburb');
      register('posCountry');
      register('posPostcode');
      register('isPosAddressValid');
      register('isManualPosAddress');
    }
  }, [register, useQasLegacyLogic]);

  /**
   * Registers representative postal address form fields when using legacy QAS logic.
   * Only active when a nominated representative type is specified.
   */
  useEffect(() => {
    if (useQasLegacyLogic) {
      register('repPostalAddress');
      register('repPostalAddressObject');
      register('repPosStreetAddress');
      register('repPosSuburb');
      register('repPosCountry');
      register('repPosPostcode');
      register('isRepPosAddressValid');
      register('isManualRepPosAddress');
    }
  }, [register, useQasLegacyLogic, nominatedRepType]);

  // Compute address values
  const isManualPhysAddress = watch('isManualPhysAddress');
  const isManualPosAddress = watch('isManualPosAddress');
  const isManualRepPosAddress = watch('isManualRepPosAddress');

  const isPhysicalCountryAU = useQasLegacyLogic && watch('phyCountry') === AUSTRALIA;
  const phyCountry = useQasLegacyLogic && (watch('phyCountry') || '');
  const phyState = useQasLegacyLogic && (watch('phyState') || '');
  const phyPostcode = useQasLegacyLogic && (watch('phyPostcode') || '');

  const isPostalCountryAU = useQasLegacyLogic && watch('posCountry') === AUSTRALIA;
  const posCountry = useQasLegacyLogic && (watch('posCountry') || '');
  const posState = useQasLegacyLogic && (watch('posState') || '');
  const posPostcode = useQasLegacyLogic && (watch('posPostcode') || '');

  const isRepPosCountryAU = useQasLegacyLogic && watch('repPosCountry') === AUSTRALIA;
  const repPosCountry = useQasLegacyLogic && (watch('repPosCountry') || '');
  const repPosState = useQasLegacyLogic && (watch('repPosState') || '');
  const repPosPostcode = useQasLegacyLogic && (watch('repPosPostcode') || '');

  const addressValues = {
    isManualPhysAddress: useQasLegacyLogic ? isManualPhysAddress : false,
    isPhysicalCountryAU,
    phyCountry,
    phyState,
    phyPostcode,
    isManualPosAddress: useQasLegacyLogic ? isManualPosAddress : false,
    isPostalCountryAU,
    posCountry,
    posState,
    posPostcode,
    isManualRepPosAddress: useQasLegacyLogic ? isManualRepPosAddress : false,
    isRepPosCountryAU,
    repPosCountry,
    repPosState,
    repPosPostcode,
  };

  // Validate and set manual physical address validity
  const hasValidManualFields =
    useQasLegacyLogic &&
    !errors.phyStreetAddress &&
    !errors.phySuburb &&
    !errors.phyCountry &&
    !errors.phyState &&
    !errors.phyPostcode;

  /**
   * Validates manual physical address entry and updates the validity flag.
   * Clears the physical address error and sets isManualPhysAddressValid based on whether
   * all required manual address fields are filled correctly.
   */
  useEffect(() => {
    if (!useQasLegacyLogic) {
      return;
    }

    if (isManualPhysAddress) {
      clearErrors('physicalAddress');

      if (hasValidManualFields) {
        setValue('isManualPhysAddressValid', hasValidManualFields);
      } else {
        setValue('isManualPhysAddressValid', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useQasLegacyLogic, isManualPhysAddress, hasValidManualFields]);

  /**
   * Triggers validation for physical address postcode when country or state changes.
   * Ensures postcode format is validated against the selected country/state combination.
   */
  useEffect(() => {
    if (useQasLegacyLogic && (phyCountry || phyState) && phyPostcode) {
      if (phyPostcode !== ' ') triggerValidation('phyPostcode');
    }
  }, [phyState, triggerValidation, phyPostcode, phyCountry, useQasLegacyLogic]);

  /**
   * Triggers validation for postal address postcode when country or state changes.
   * Ensures postcode format is validated against the selected country/state combination.
   */
  useEffect(() => {
    if (useQasLegacyLogic && (posCountry || posState) && posPostcode) {
      if (posPostcode !== ' ') triggerValidation('posPostcode');
    }
  }, [posPostcode, posState, triggerValidation, posCountry, useQasLegacyLogic]);

  /**
   * Triggers validation for representative postal address postcode when country or state changes.
   * Ensures postcode format is validated against the selected country/state combination.
   */
  useEffect(() => {
    if (useQasLegacyLogic && (repPosCountry || repPosState) && repPosPostcode) {
      if (repPosPostcode !== ' ') triggerValidation('repPosPostcode');
    }
  }, [repPosPostcode, repPosState, triggerValidation, repPosCountry, useQasLegacyLogic]);

  /**
   * Conditionally registers the physical state field when manual address entry is active
   * and the selected country is Australia (where state is required).
   */
  useEffect(() => {
    if (useQasLegacyLogic && isManualPhysAddress && isPhysicalCountryAU) register('phyState');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useQasLegacyLogic, isManualPhysAddress, isPhysicalCountryAU]);

  /**
   * Conditionally registers the postal state field when manual address entry is active
   * and the selected country is Australia (where state is required).
   */
  useEffect(() => {
    if (useQasLegacyLogic && isManualPosAddress && isPostalCountryAU) register('posState');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useQasLegacyLogic, isManualPosAddress, isPostalCountryAU]);

  /**
   * Conditionally registers the representative postal state field when manual address entry is active
   * and the selected country is Australia (where state is required).
   */
  useEffect(() => {
    if (useQasLegacyLogic && isManualRepPosAddress && isRepPosCountryAU) register('repPosState');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useQasLegacyLogic, isManualRepPosAddress, isRepPosCountryAU]);
  // TODO: QAS_CLEANUP - Remove all code above until "Address management integration" comment after toggle decommission - END

  // Address management integration
  const { processAddressChange, getDefaults } = useAddressChange();
  const physicalDefaults = getDefaults({ type: 'physical', formValues: watch() });
  const postalDefaults = getDefaults({ type: 'postal', formValues: watch() });
  const repPostalDefaults = getDefaults({ type: 'repPostal', formValues: watch() });

  const handleAddressChange = createAddressChangeHandler(setValue, processAddressChange);

  return {
    ...addressValues,
    handleAddressChange,
    physicalDefaults,
    postalDefaults,
    repPostalDefaults,
  };
};
